(ns hai.notes.facets
  "The notes-wall filter surfaces: the facet sidebar — the one place filters
   are set — and the applied-filter chips shown in the page header. Both drive
   the collection's standard `:table/filters` form (sub-field `set-value!` for
   the sidebar, the map-level field for the chips), so the query plumbing is
   untouched. Options and their counts come from `:user/note-facets` via
   [[hai.notes.data/load-facets]]."
  (:require
   [hai.notes.data :as data]
   [hai.ui.components.collection.filters :as filters]
   [hai.ui.icons :as icons]
   [hai.ui.rf-sugar :refer [<s >e]]
   [hai.ui.tokens :as t]
   [lambdaisland.ornament :as o]
   [reagent.core :as r]))

(def ^:private tag-icon (icons/mui-icon :outlined :label))
(def ^:private person-icon (icons/mui-icon :outlined :person))
(def ^:private public-icon (icons/mui-icon :outlined :public))
(def ^:private people-icon (icons/mui-icon :outlined :people))
(def ^:private chevron-icon (icons/mui-icon :outlined :chevron_right))

;; ---------------------------------------------------------------------------
;; Applied-filter chips (page header)
;; ---------------------------------------------------------------------------

(o/defstyled chips-row :div
  {:display "flex" :align-items "center" :flex-wrap "wrap"
   :gap "6px" :min-width "0"})

(o/defstyled filter-chip :span
  {:display "inline-flex" :align-items "center" :gap "6px"
   :height "26px" :padding "0 4px 0 10px"
   :border-radius "999px"
   :border-width "1px" :border-style "solid" :border-color t/--note-sage-35
   :background t/--colour-snow
   :font-size "12px" :color t/--note-kraft
   :white-space "nowrap"}
  [:b {:font-weight "600"}]
  [:.lead-icon {:display "inline-flex"}
   [:svg {:width "13px" :height "13px" :fill "currentColor"}]]
  [:.remove
   {:display "inline-flex" :align-items "center" :justify-content "center"
    :width "18px" :height "18px" :border "none" :border-radius "999px"
    :background "transparent" :padding "0"
    :color t/--note-kraft :cursor "pointer"}
   [:&:hover {:background t/--note-kraft-10}]
   [:svg {:width "11px" :height "11px" :fill "currentColor"}]])

(defn applied-chips
  "The header's row of currently-applied filters — one chip per filter column
   holding a value, showing the column's pill face (`:label`/`:icon`/`:summary`)
   with an × that resets it. Clearing sets the field on the filters SUB-form
   (id `[<root-form-id> :table/filters]`) — the same path the sidebar writes —
   because sub-form values propagate up to the root form but a root-level write
   never reaches the sub-form's fields, which would leave the sidebar's
   selection highlight stale. Renders nothing when no filter is applied."
  [table]
  (let [columns (<s [::filters/columns table])
        filters-form-id [(-> table :table/form :form/id) :table/filters]
        {:form.field/keys [current-value]}
        (get-in table [:table/form :form/fields :table/filters])
        applied (for [{:table.column/keys [attr filter] :as column} @columns
                      :let [{:table.column.filter/keys [label icon summary]} filter
                            value (get current-value attr)]
                      :when (and label (some? value))]
                  ^{:key attr}
                  [filter-chip
                   (when icon [:span.lead-icon [icon]])
                   [:b (str label ":")]
                   [:span (when summary (summary value column))]
                   [:button.remove
                    {:type "button"
                     :aria-label (str "Reset " label " filter")
                     :on-click (fn []
                                 (>e [:form.field/set-value! filters-form-id attr nil])
                                 (>e [::data/load-facets]))}
                    [icons/outlined-close]]])]
    (when (seq applied)
      (into [chips-row] applied))))

;; ---------------------------------------------------------------------------
;; Facet sidebar (cards view)
;; ---------------------------------------------------------------------------

(o/defstyled sidebar :aside
  {:width "240px"
   :position "sticky" :top "76px"
   :align-self "start"
   :max-height "calc(100vh - 200px)"
   :overflow-y "auto"
   :display "flex" :flex-direction "column" :gap "16px"
   :padding "14px 12px"
   :border-width "1px" :border-style "solid" :border-color t/--note-sage-35
   :border-radius "10px"
   :background t/--colour-snow
   :scrollbar-width "thin"
   :scrollbar-color "rgba(74,93,63,0.4) transparent"}
  [:.facet-group {:display "flex" :flex-direction "column" :gap "6px"}]
  [:.facet-heading
   {:display "flex" :align-items "center" :gap "6px"
    :font-size "11px" :font-weight "700"
    :letter-spacing "0.06em" :text-transform "uppercase"
    :color t/--note-sage-80}
   [:svg {:width "13px" :height "13px" :fill "currentColor"}]]
  [:.facet-options {:display "flex" :flex-direction "column" :gap "2px"}]
  [:.facet-option
   {:display "flex" :align-items "center" :gap "8px"
    :width "100%" :text-align "left"
    :padding "5px 8px" :border "none" :border-radius "7px"
    :background "transparent"
    :font-family "inherit" :font-size "13px" :font-weight "500"
    :color t/--note-kraft :cursor "pointer"
    :min-width "0"}
   [:&:hover {:background t/--note-kraft-10}]
   [:&.selected {:background t/--note-kraft-10 :font-weight "700"}]
   [:&.zero {:opacity "0.45"}]
   [:.opt-label
    {:flex "1" :min-width "0"
     :overflow "hidden" :text-overflow "ellipsis" :white-space "nowrap"}]
   [:.opt-count {:flex "0 0 auto" :font-size "12px" :font-weight "500"
                 :color t/--note-sage-80}]]
  ;; Domain rows put an expand chevron beside the selectable row.
  [:.domain-row {:display "flex" :align-items "center" :min-width "0"}
   [:.facet-option {:flex "1"}]]
  [:.chevron
   {:display "inline-flex" :align-items "center" :justify-content "center"
    :width "20px" :height "20px" :flex "0 0 auto"
    :border "none" :border-radius "6px"
    :background "transparent" :padding "0"
    :color t/--note-sage-80 :cursor "pointer"}
   [:&:hover {:background t/--note-kraft-10}]
   [:svg {:width "15px" :height "15px" :fill "currentColor"
          :transition "transform 0.15s ease"}]
   [:&.open [:svg {:transform "rotate(90deg)"}]]]
  [:.child {:padding-left "26px"}]
  [:.clear-all
   {:align-self "flex-start"
    :border "none" :background "transparent" :padding "0 8px"
    :font-family "inherit" :font-size "12px" :font-weight "600"
    :color t/--note-sage-80 :cursor "pointer"}
   [:&:hover {:color t/--note-kraft :text-decoration "underline"}]]
  [:at-media {:max-width "768px"}
   {:position "static" :width "100%" :max-height "none"}])

(defn- facet-option
  [{:keys [label amount selected? on-toggle child? title]}]
  [:button.facet-option
   {:type     "button"
    :class    (cond-> []
                selected? (conj "selected")
                child?    (conj "child")
                ;; Nothing matches this option under the current constraints.
                (and (some? amount) (zero? amount) (not selected?)) (conj "zero"))
    :title    (or title label)
    :on-click on-toggle}
   [:span.opt-label label]
   (when (some? amount) [:span.opt-count amount])])

(defn- toggle-value!
  "Single-select facet click: picking the selected option again clears it.
   The counts re-query after — the set event is processed first, so the
   reload sees the new value."
  [set-value! current-value value]
  (set-value! (if (= current-value value) nil value))
  (>e [::data/load-facets]))

(defn- field-state [table attr]
  (get-in table [:table/form :form/fields attr]))

(defn- clear-all!
  [table]
  (doseq [[_ {:form.field/keys [set-value! current-value]}]
          (-> table :table/form :form/fields)]
    (when (and set-value! (some? current-value))
      (set-value! nil)))
  (>e [::data/load-facets]))

(defn- tags-group [table tags untagged-count]
  (let [{:form.field/keys [current-value set-value!]} (field-state table :note/tags)]
    [:section.facet-group
     [:div.facet-heading [tag-icon] "Tag"]
     (into [:div.facet-options
            [facet-option {:label     "Untagged"
                           :amount    untagged-count
                           :selected? (= current-value :untagged)
                           :on-toggle #(toggle-value! set-value! current-value :untagged)}]]
           (for [{tag-name :tag/name amount :facet/count} tags
                 :let [value [tag-name]]]
             ^{:key tag-name}
             [facet-option {:label     (str "#" tag-name)
                            :amount    amount
                            :selected? (= current-value value)
                            :on-toggle #(toggle-value! set-value! current-value value)}]))]))

(defn- contacts-group [table contacts no-contact-count]
  (let [{:form.field/keys [current-value set-value!]} (field-state table :note/contacts)]
    [:section.facet-group
     [:div.facet-heading [person-icon] "Contact"]
     (into [:div.facet-options
            [facet-option {:label     "No contact"
                           :amount    no-contact-count
                           :selected? (= current-value :no-contact)
                           :on-toggle #(toggle-value! set-value! current-value :no-contact)}]]
           (for [{contact-uuid :contact/uuid full-name :contact/full-name
                  amount :facet/count} contacts]
             ^{:key contact-uuid}
             [facet-option {:label     full-name
                            :amount    amount
                            :selected? (= current-value contact-uuid)
                            :on-toggle #(toggle-value! set-value! current-value contact-uuid)}]))]))

(defn- domains-group [table domains no-web-page-count !open-domains]
  (let [{:form.field/keys [current-value set-value!]} (field-state table :note/url-domain)]
    [:section.facet-group
     [:div.facet-heading [public-icon] "Domain"]
     (into [:div.facet-options
            [facet-option {:label     "No web page"
                           :amount    no-web-page-count
                           :selected? (= current-value [:none ""])
                           :on-toggle #(toggle-value! set-value! current-value [:none ""])}]]
           (for [{domain-name :domain/name urls :domain/urls
                  amount :facet/count} domains
                 :let [open? (contains? @!open-domains domain-name)
                       value [:domain domain-name]]]
             ^{:key domain-name}
             [:<>
              [:div.domain-row
               [:button.chevron
                {:type       "button"
                 :class      (when open? "open")
                 :aria-label (str (if open? "Collapse " "Expand ") domain-name)
                 :on-click   #(swap! !open-domains (if open? disj conj) domain-name)}
                [chevron-icon]]
               [facet-option {:label     domain-name
                              :amount    amount
                              :selected? (= current-value value)
                              :on-toggle #(toggle-value! set-value! current-value value)}]]
              (when open?
                (into [:<>]
                      (for [{url-value :url/value url-label :url/label
                             url-amount :facet/count} urls
                            :let [value [:url url-value]]]
                        ^{:key url-value}
                        [facet-option {:label     url-label
                                       :title     url-value
                                       :amount    url-amount
                                       :child?    true
                                       :selected? (= current-value value)
                                       :on-toggle #(toggle-value! set-value! current-value value)}])))]))]))

(defn- shared-group [table shared not-shared-count]
  (let [{:form.field/keys [current-value set-value!]} (field-state table :note/shared-with-user)]
    [:section.facet-group
     [:div.facet-heading [people-icon] "Shared"]
     (into [:div.facet-options
            [facet-option {:label     "Not shared"
                           :amount    not-shared-count
                           :selected? (= current-value :not-shared)
                           :on-toggle #(toggle-value! set-value! current-value :not-shared)}]]
           (for [{partner-uuid :user/uuid partner-label :facet/label
                  amount :facet/count} shared]
             ^{:key partner-uuid}
             [facet-option {:label     partner-label
                            :amount    amount
                            :selected? (= current-value partner-uuid)
                            :on-toggle #(toggle-value! set-value! current-value partner-uuid)}]))]))

(defn- sidebar-panel [table]
  (r/with-let [_ (>e [::data/load-facets])
               !open-domains (r/atom #{})]
    (let [{:facet/keys [tags contacts domains shared
                        no-web-page-count not-shared-count
                        untagged-count no-contact-count]} @(<s [::data/facets])
          any-applied? (some (fn [[_ {:form.field/keys [current-value]}]]
                               (some? current-value))
                             (-> table :table/form :form/fields))]
      [sidebar
       [tags-group table tags untagged-count]
       [contacts-group table contacts no-contact-count]
       [domains-group table domains no-web-page-count !open-domains]
       [shared-group table shared not-shared-count]
       (when any-applied?
         [:button.clear-all
          {:type "button" :on-click #(clear-all! table)}
          "Clear all filters"])])))

(defn panel
  "`:filters/render` for the notes collection: the facet sidebar, in both
   views. Cards view mounts it as a sibling of the card grid; table view does
   the same via the collection's `:filters/placement :sibling` — the page grid
   places either one in the left column."
  [table]
  [sidebar-panel table])
